/**
 * How many vouchers to buy.
 *
 * A stepper rather than a free number input. On a phone a numeric field opens a
 * keyboard to buy three coffees, and it accepts "0", "-1" and "999" — each of
 * which then has to be rejected somewhere. Two buttons and a readout cannot
 * express an invalid value in the first place.
 *
 * THE LIMIT HERE IS A COURTESY, NOT A CONTROL. The server enforces maxPerUser
 * and stock in OrderService.GuardPerUserLimitAsync, counting orders this shopper
 * already has. This only stops someone reaching a refusal they could have been
 * told about earlier — it does not decide anything.
 */
export function QuantityPicker({
  value,
  max,
  onChange,
}: {
  value: number;
  max: number | null;
  onChange: (next: number) => void;
}) {
  const ceiling = max ?? 10;
  const canAdd = value < ceiling;
  const canRemove = value > 1;

  return (
    <div className="qty">
      <span id="qty-label" className="qty-label">Quantity</span>

      <div className="qty-controls" role="group" aria-labelledby="qty-label">
        <button
          type="button"
          className="qty-btn"
          onClick={() => onChange(value - 1)}
          disabled={!canRemove}
          aria-label="One fewer"
        >
          −
        </button>

        {/*
          aria-live so the number is ANNOUNCED when it changes. Without it a
          screen reader user presses the button and hears nothing at all, because
          focus stays on the button and the text that changed is elsewhere.
        */}
        <span className="qty-value" aria-live="polite" aria-atomic="true">
          {value}
        </span>

        <button
          type="button"
          className="qty-btn"
          onClick={() => onChange(value + 1)}
          disabled={!canAdd}
          aria-label="One more"
        >
          +
        </button>
      </div>

      {max !== null && value >= max && (
        <span className="hint">Limit {max} per customer</span>
      )}
    </div>
  );
}
